import type { Attachment, PlanDetail, SongDetail } from "../../../shared/types";
import { apiFetch } from "../../api";
import { getSong as fetchSong } from "../songs/music-api";
import { getFile, getPlan, getSong, putFile, putPlan, putSong, type CachedFile } from "./cache";

export interface OfflineFileProblem {
  id: string;
  label: string;
  reason: string;
}

export interface OfflineReadiness {
  ready: boolean;
  planCached: boolean;
  total: number;
  available: number;
  missing: OfflineFileProblem[];
}

export interface OfflineDownloadProgress {
  total: number;
  completed: number;
  cached: number;
  downloaded: number;
  failed: number;
  ready: boolean;
  errors: OfflineFileProblem[];
}

export type OfflineDownloadReport = OfflineDownloadProgress;

export interface OfflineDownloadOptions {
  signal?: AbortSignal;
  onProgress?: (progress: OfflineDownloadProgress) => void;
}

function requiredFiles(plan: PlanDetail): Attachment[] {
  const unique = new Map<string, Attachment>();
  for (const item of plan.items) {
    for (const file of item.attachments) {
      if (file.kind === "chart-pdf" || file.kind === "audio") unique.set(file.id, file);
    }
  }
  return [...unique.values()];
}

function requiredSongs(plan: PlanDetail): { id: string; title: string }[] {
  const unique = new Map<string, { id: string; title: string }>();
  for (const item of plan.items) if (item.songId) unique.set(item.songId, { id: item.songId, title: item.title });
  return [...unique.values()];
}

function songIsReady(song: SongDetail | null, id: string): boolean {
  return song?.id === id && Array.isArray(song.arrangements) && Array.isArray(song.attachments);
}

function planMatches(cached: PlanDetail | null, current: PlanDetail): boolean {
  if (!cached) return false;
  if (cached.serviceTypeName !== "Practice") return JSON.stringify(cached) === JSON.stringify(current);
  // Practice plans are synthesized with a new timestamp each time a song opens.
  return cached.id === current.id && JSON.stringify(cached.items.map((item) => ({ ...item, startsAt: "" })))
    === JSON.stringify(current.items.map((item) => ({ ...item, startsAt: "" })));
}

function problem(file: Attachment, reason: string): OfflineFileProblem {
  return { id: file.id, label: file.label || file.filename, reason };
}

/** Reject incomplete downloads and login/error HTML accidentally returned as file content. */
export function fileIntegrityError(file: Attachment, bytes: ArrayBuffer): string | null {
  if (!(bytes instanceof ArrayBuffer) || bytes.byteLength === 0) return "The file is empty.";
  if (file.bytes > 0 && bytes.byteLength !== file.bytes) return "The downloaded size does not match the attachment.";
  const prefix = new TextDecoder().decode(bytes.slice(0, Math.min(bytes.byteLength, 1024))).trimStart().toLowerCase();
  if (prefix.startsWith("<!doctype html") || prefix.startsWith("<html")) return "The server returned a page instead of a file.";
  if (file.kind === "chart-pdf" && !prefix.includes("%pdf-")) return "The chart is not a valid PDF.";
  return null;
}

async function cachedFileIsReady(file: Attachment): Promise<boolean> {
  const stored = await getFile(file.id);
  return !!stored && stored.id === file.id && !fileIntegrityError(file, stored.bytes);
}

export async function getOfflineReadiness(plan: PlanDetail): Promise<OfflineReadiness> {
  const files = requiredFiles(plan);
  const songs = requiredSongs(plan);
  const cachedPlan = await getPlan(plan.id).catch(() => null);
  const planCached = planMatches(cachedPlan, plan);
  const missing: OfflineFileProblem[] = [];
  for (const song of songs) {
    try {
      if (!songIsReady(await getSong(song.id), song.id)) missing.push({ id: song.id, label: song.title, reason: "Song details are not saved on this device." });
    } catch {
      missing.push({ id: song.id, label: song.title, reason: "Could not read this device's song cache." });
    }
  }
  for (const file of files) {
    try {
      if (!(await cachedFileIsReady(file))) missing.push(problem(file, "Not saved on this device."));
    } catch {
      missing.push(problem(file, "Could not read this device's file cache."));
    }
  }
  const total = songs.length + files.length;
  return { ready: planCached && missing.length === 0, planCached, total, available: total - missing.length, missing };
}

async function downloadFile(file: Attachment, signal?: AbortSignal): Promise<CachedFile> {
  if (!file.url) throw new Error("The attachment has no download URL.");
  const response = await apiFetch(file.url, { cache: "no-store", signal });
  if (!response.ok) throw new Error(`Download failed (HTTP ${response.status}).`);
  const bytes = await response.arrayBuffer();
  const invalid = fileIntegrityError(file, bytes);
  if (invalid) throw new Error(invalid);
  return { id: file.id, mime: file.mime, filename: file.filename, bytes, savedAt: new Date().toISOString() };
}

export async function downloadPlanForOffline(plan: PlanDetail, options: OfflineDownloadOptions = {}): Promise<OfflineDownloadReport> {
  const files = requiredFiles(plan);
  const songs = requiredSongs(plan);
  const report: OfflineDownloadReport = { total: files.length + songs.length, completed: 0, cached: 0, downloaded: 0, failed: 0, ready: false, errors: [] };
  const emit = () => options.onProgress?.({ ...report, errors: [...report.errors] });
  try {
    options.signal?.throwIfAborted();
    await putPlan(plan);
  } catch (error) {
    if (options.signal?.aborted) throw error;
    report.errors.push({ id: plan.id, label: plan.title, reason: "Could not save the plan on this device." });
  }
  emit();
  for (const song of songs) {
    options.signal?.throwIfAborted();
    try {
      if (songIsReady(await getSong(song.id), song.id)) {
        report.cached += 1;
      } else {
        const detail = await fetchSong(song.id);
        if (!songIsReady(detail, song.id)) throw new Error("Song details are incomplete.");
        await putSong(detail);
        if (!songIsReady(await getSong(song.id), song.id)) throw new Error("Song details did not persist on this device.");
        report.downloaded += 1;
      }
    } catch (error) {
      if (options.signal?.aborted) throw error;
      report.failed += 1;
      report.errors.push({ id: song.id, label: song.title, reason: error instanceof Error ? error.message : "Could not save this song." });
    }
    report.completed += 1;
    emit();
  }
  for (const file of files) {
    options.signal?.throwIfAborted();
    try {
      if (await cachedFileIsReady(file)) {
        report.cached += 1;
      } else {
        await putFile(await downloadFile(file, options.signal));
        if (!(await cachedFileIsReady(file))) throw new Error("The file did not persist on this device.");
        report.downloaded += 1;
      }
    } catch (error) {
      if (options.signal?.aborted) throw error;
      report.failed += 1;
      report.errors.push(problem(file, error instanceof Error ? error.message : "Could not save this file."));
    }
    report.completed += 1;
    emit();
  }
  const readiness = await getOfflineReadiness(plan);
  report.ready = readiness.ready;
  if (!readiness.planCached && !report.errors.some((entry) => entry.id === plan.id)) {
    report.errors.push({ id: plan.id, label: plan.title, reason: "The plan was not saved on this device." });
  }
  emit();
  return report;
}
